import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { API_COMMON_CACHE_TIME, HELPER_OPTION_MAX_ROWS } from '@/utils/consts'
import { SelectOptionType } from '@/components/content/SelectBoxTypes'
import { getShipperListV2 } from '@/services/shipper/apis'
import { getErpHistoryItems, getErpHistorySummaries, getGoodsCodeOptions, getStyleNumberOptions } from './apis'
import { ErpHistoryItem, ErpHistorySummary, GetErpHistoriesReq, GetGoodsCodeOptionsReq, GetStyleNumberOptionsReq, StyleNumberOption } from './types'

const PATH = ['inventory', 'stockMoveHistory'] as const

export const stockMoveHistoryQueryKeys = {
  all: PATH,
  summaries: (params: GetErpHistoriesReq) => [...PATH, 'SUMMARIES', params] as const,
  items: (params: GetErpHistoriesReq) => [...PATH, 'ITEMS', params] as const,
  shippers: [...PATH, 'SHIPPERS'] as const,
  styleNumbers: (params: GetStyleNumberOptionsReq) => [...PATH, 'STYLE_NUMBERS', params] as const,
  goodsCodes: (params: GetGoodsCodeOptionsReq) => [...PATH, 'GOODS_CODES', params] as const,
}

/**
 * EST05 HEAD(문서 헤더) 목록.
 *
 * 진입 즉시 조회하지 않는다(`enabled: false`) — 조회 버튼으로만 실행한다(ST02·ST10·ST11 과 동일).
 * 실패(HTTP 400)는 reject 로 오므로 호출부가 refetch 반환값의 error 에서 message 를 꺼내 알럿한다.
 */
export const useGetErpHistorySummaries = (params: GetErpHistoriesReq) => {
  const { data, ...rest } = useQuery({
    queryKey: stockMoveHistoryQueryKeys.summaries(params),
    queryFn: async () => {
      const res = await getErpHistorySummaries(params)
      return res.data?.items ?? []
    },
    enabled: false,
  })

  return { items: (data ?? []) as ErpHistorySummary[], ...rest }
}

/**
 * EST05 ITEM(문서 상세) 목록 — **선택한 HEAD 문서 기준 서버 조회**.
 *
 * 조회 버튼으로는 HEAD 만 채우고, HEAD 행을 클릭하면 그 문서(ordNo)로 상세를 받아 온다
 * (1.0 EST03 이 행 더블클릭으로 EST03_ITEM 을 조회하던 흐름 · EST04 와 동일 구조. 리뷰 피드백 2026-08-06).
 * 문서별 캐시라 행을 오갈 때 재조회가 없다.
 */
export const useGetErpHistoryItems = (params: GetErpHistoriesReq) => {
  const { data, ...rest } = useQuery({
    queryKey: stockMoveHistoryQueryKeys.items(params),
    queryFn: async () => {
      const res = await getErpHistoryItems(params)
      return res.data?.items ?? []
    },
    enabled: Boolean(params.ordNo?.length),
  })

  return { items: (data ?? []) as ErpHistoryItem[], ...rest }
}

/**
 * 화주명 셀렉트 옵션 — `POST /api/v1/get-shippers` 전체 목록(코드/명).
 *
 * WEB_STO 의 SPR_NM 은 화주**코드**다(1.0 EST01 UpdateForm 이 `shipper_code=spr_nm` 으로 역참조) —
 * value=코드, label=화주명으로 만든다(ST10·ST11 과 동일 지점).
 */
export const useGetShipperOptions = () => {
  const { data, ...rest } = useQuery({
    queryKey: stockMoveHistoryQueryKeys.shippers,
    queryFn: async (): Promise<SelectOptionType<string>[]> => {
      // 클라이언트 검색(showSearch) 전제로 전건을 한 번에 받는다(get-shipper-names 의 size=10000 선례).
      const res = await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: 10000 })
      return (res?.data?.items ?? []).map((s) => ({ value: s.shipperCode, label: s.shipperName || s.shipperCode }))
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { options: data ?? [], ...rest }
}

interface StyleNumberOptionsData {
  items: StyleNumberOption[]
  /** 응답이 BE 상한(1,000행)에 닿았다 — 화면이 "조건을 좁혀 달라" 안내를 띄운다. */
  isTruncated: boolean
}

/**
 * 스타일넘버 서치헬퍼 목록. 조회 조건(화주·브랜드명)별로 캐시한다.
 * `enabled`(모달 open + 모달 안 조회 클릭)로 게이트한다 — 모달을 열자마자 조회하지 않는다(2026-08-04 피드백).
 *
 * 2.0 응답(styleNumber·brandName·itemCode)을 모달 표 컬럼 키(STL_NO·BRD_NM·GDS_CD)로 바꿔 돌려준다. 잘림 판정은 거르기 전 건수로 한다.
 * `isTruncated` 는 응답이 BE 상한(1,000행)에 닿았다는 뜻이다.
 */
export const useGetStyleNumberOptions = (
  params: GetStyleNumberOptionsReq,
  enabled = true,
): Omit<UseQueryResult<StyleNumberOptionsData, Error>, 'data'> & StyleNumberOptionsData => {
  const { data, ...rest } = useQuery({
    queryKey: stockMoveHistoryQueryKeys.styleNumbers(params),
    queryFn: async (): Promise<StyleNumberOptionsData> => {
      const rows = (await getStyleNumberOptions(params)).data?.items ?? []
      return {
        items: rows
          .filter((r) => r.styleNumber) // 스타일번호가 없는 상품도 distinct 1행으로 온다 — 골라도 조건이 비므로 뺀다(브랜드 헬퍼의 NULL 제외와 같다)
          .map((r) => ({ STL_NO: r.styleNumber ?? '', BRD_NM: r.brandName ?? '', GDS_CD: r.itemCode ?? '' })),
        isTruncated: rows.length >= HELPER_OPTION_MAX_ROWS,
      }
    },
    enabled,
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data?.items ?? [], isTruncated: data?.isTruncated ?? false, ...rest }
}

/**
 * 상품코드 서치헬퍼 목록. 조회 조건(화주·브랜드명·상품명)별로 캐시한다.
 * `enabled`(모달 open + 모달 안 조회 클릭)로 게이트한다 — 모달을 열자마자 조회하지 않는다(2026-08-04 피드백).
 */
export const useGetGoodsCodeOptions = (params: GetGoodsCodeOptionsReq, enabled = true) => {
  const { data, ...rest } = useQuery({
    queryKey: stockMoveHistoryQueryKeys.goodsCodes(params),
    queryFn: async () => {
      const res = await getGoodsCodeOptions(params)
      return res.data?.items ?? []
    },
    enabled,
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data ?? [], ...rest }
}
